import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Camera, CalendarDays, MapPin, Clock, Users, CheckCircle2,
  Plus, LogOut, Search, Filter, ChevronLeft, ChevronRight,
  X, Play, XCircle, Trash2, Edit2, Link2, FileText, AlertCircle
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';
import ChangeHistoryButton from '../components/ChangeHistoryButton';

import API from '../utils/api';
const SHOOT_TYPES = ['Content Shoot', 'Product Shoot', 'Ad Shoot'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function ShootPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canWrite = user?.role === 'admin' || user?.effective_permissions?.shoots === 'edit';
  const [shoots, setShoots] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedShoot, setSelectedShoot] = useState(null);
  const [view, setView] = useState('calendar'); // 'calendar' | 'list'
  const [calView, setCalView] = useState('month'); // 'month' | 'week'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Filters
  const [fClient, setFClient] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fTeam, setFTeam] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fClient && fClient !== 'all') params.client_id = fClient;
      if (fStatus && fStatus !== 'all') params.status = fStatus;
      if (fTeam && fTeam !== 'all') params.team_member = fTeam;

      const [shootsRes, statsRes, clientsRes, empRes] = await Promise.all([
        axios.get(`${API}/shoots`, { withCredentials: true, params }),
        axios.get(`${API}/shoots/stats`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
      ]);
      setShoots(shootsRes.data);
      setStats(statsRes.data);
      setClients(clientsRes.data);
      setEmployees(empRes.data.filter(e => e.status === 'Active'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [fClient, fStatus, fTeam]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading && !stats) {
    return <SidebarLayout title="Shoots" subtitle="Shoot Tracker"><div className="flex items-center justify-center py-20"><div className="text-white text-lg">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Shoots" subtitle="Shoot Tracker">
        {/* KPI Cards */}
        <section className="mb-8 animate-fadeIn">
          <div className="kpi-scroll md:grid-cols-4 gap-4">
            <KPICard icon={<Camera className="text-purple-400" />} label="This Month" value={stats?.total_this_month || 0} color="purple" testId="kpi-total" />
            <KPICard icon={<CalendarDays className="text-cyan-400" />} label="Upcoming" value={stats?.upcoming || 0} color="cyan" testId="kpi-upcoming" />
            <KPICard icon={<CheckCircle2 className="text-emerald-400" />} label="Completed" value={stats?.completed_this_month || 0} color="emerald" testId="kpi-completed" />
            <KPICard icon={<Clock className="text-amber-400" />} label="Pending" value={stats?.pending || 0} color="amber" testId="kpi-pending" />
          </div>
        </section>

        {/* Controls: View Toggle + Filters + Create */}
        <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex bg-black/40 rounded-lg border border-white/10 p-0.5">
                <button onClick={() => setView('calendar')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'calendar' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid="view-calendar">Calendar</button>
                <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid="view-list">List</button>
              </div>
              {view === 'calendar' && (
                <div className="flex bg-black/40 rounded-lg border border-white/10 p-0.5 ml-2">
                  <button onClick={() => setCalView('month')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${calView === 'month' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid="cal-month">Month</button>
                  <button onClick={() => setCalView('week')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${calView === 'week' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid="cal-week">Week</button>
                </div>
              )}
            </div>
            <div className="flex gap-3 flex-wrap items-center">
              <Select value={fClient} onValueChange={setFClient}>
                <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="w-[130px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Planned">Planned</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fTeam} onValueChange={setFTeam}>
                <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-team"><SelectValue placeholder="Team Member" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                  <SelectItem value="all">All Members</SelectItem>
                  {employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {canWrite && <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)] h-9 text-xs" data-testid="create-shoot-button">
                <Plus size={16} className="mr-1" /> New Shoot
              </Button>}
            </div>
          </div>
        </section>

        {/* Calendar / List View */}
        {view === 'calendar' ? (
          <CalendarView
            shoots={shoots}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            calView={calView}
            onShootClick={(s) => setSelectedShoot(s)}
            formatDate={formatDate}
          />
        ) : (
          <ListView shoots={shoots} onShootClick={(s) => setSelectedShoot(s)} formatDate={formatDate} user={user} />
        )}

        {/* Upcoming + Recent Tables */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3 flex items-center gap-2">
              <CalendarDays size={14} className="text-cyan-400" /> Upcoming Shoots
            </h3>
            {stats?.upcoming_shoots?.length === 0 ? (
              <p className="text-xs text-slate-600 py-4 text-center">No upcoming shoots</p>
            ) : (
              <div className="space-y-1">
                {stats?.upcoming_shoots?.map(s => (
                  <button key={s.id} onClick={() => setSelectedShoot(s)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white/[0.03] transition-colors text-left" data-testid={`upcoming-${s.shoot_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-8 rounded-full bg-cyan-500/40" />
                      <div>
                        <p className="text-sm text-white font-medium">{s.client_name}</p>
                        <p className="text-[10px] text-slate-500">{formatDate(s.shoot_date)} · {s.location}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {s.team?.slice(0, 3).map((t, i) => (
                        <span key={i} className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded text-slate-400">{t.name?.split(' ')[0]}</span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-400" /> Recent Completed
            </h3>
            {stats?.recent_completed?.length === 0 ? (
              <p className="text-xs text-slate-600 py-4 text-center">No completed shoots</p>
            ) : (
              <div className="space-y-1">
                {stats?.recent_completed?.map(s => (
                  <button key={s.id} onClick={() => setSelectedShoot(s)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white/[0.03] transition-colors text-left" data-testid={`recent-${s.shoot_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-8 rounded-full bg-emerald-500/40" />
                      <div>
                        <p className="text-sm text-white font-medium">{s.client_name}</p>
                        <p className="text-[10px] text-slate-500">{formatDate(s.shoot_date)} · {s.location}</p>
                      </div>
                    </div>
                    <span className="status-badge completed text-[10px]">Completed</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

      {showCreateDialog && (
        <CreateShootDialog clients={clients} employees={employees} onClose={() => setShowCreateDialog(false)} onCreated={() => { setShowCreateDialog(false); fetchData(); }} />
      )}
      {selectedShoot && (
        <ShootDetailSheet shoot={selectedShoot} employees={employees} onClose={() => setSelectedShoot(null)} onUpdate={() => { setSelectedShoot(null); fetchData(); }} formatDate={formatDate} canWrite={canWrite} />
      )}
    </SidebarLayout>
  );
}

// ================================
// KPI CARD
// ================================
function KPICard({ icon, label, value, color, testId }) {
  const bgColors = { purple: 'border-purple-500/20', cyan: 'border-cyan-500/20', emerald: 'border-emerald-500/20', amber: 'border-amber-500/20' };
  return (
    <div className={`kpi-card border-l-4 ${bgColors[color]}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>
        {icon}
      </div>
      <div className="font-heading text-3xl font-black tracking-tight text-white">{value}</div>
    </div>
  );
}

// ================================
// CALENDAR VIEW
// ================================
function CalendarView({ shoots, currentDate, setCurrentDate, calView, onShootClick, formatDate }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const navigateMonth = (dir) => {
    const d = new Date(currentDate);
    if (calView === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCurrentDate(d);
  };

  // Build shoot map by date
  const shootMap = {};
  shoots.forEach(s => {
    if (!shootMap[s.shoot_date]) shootMap[s.shoot_date] = [];
    shootMap[s.shoot_date].push(s);
  });

  const statusColor = (status) => {
    if (status === 'Completed') return 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300';
    if (status === 'Cancelled') return 'bg-rose-500/20 border-rose-500/30 text-rose-300';
    return 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300';
  };

  if (calView === 'month') {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date().toISOString().slice(0, 10);
    const cells = [];

    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div className="glass-card overflow-hidden animate-fadeIn" style={{ animationDelay: '0.2s' }} data-testid="calendar-month-view">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <button onClick={() => navigateMonth(-1)} className="text-slate-400 hover:text-white p-1" data-testid="cal-prev"><ChevronLeft size={18} /></button>
          <h3 className="text-white font-heading font-bold text-lg">{MONTHS[month]} {year}</h3>
          <button onClick={() => navigateMonth(1)} className="text-slate-400 hover:text-white p-1" data-testid="cal-next"><ChevronRight size={18} /></button>
        </div>
        <div className="overflow-x-auto">
        <div className="grid grid-cols-7 min-w-[560px]">
          {DAYS.map(d => (
            <div key={d} className="py-2 text-center text-[10px] font-semibold tracking-widest uppercase text-slate-500 border-b border-white/5">{d}</div>
          ))}
          {cells.map((day, idx) => {
            const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
            const dayShoots = dateStr ? (shootMap[dateStr] || []) : [];
            const isToday = dateStr === today;

            return (
              <div key={idx} className={`min-h-[90px] border-b border-r border-white/[0.03] p-1.5 ${day ? 'hover:bg-white/[0.02]' : ''}`}>
                {day && (
                  <>
                    <span className={`text-xs font-medium ${isToday ? 'bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-slate-400'}`}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayShoots.slice(0, 3).map(s => (
                        <button key={s.id} onClick={() => onShootClick(s)} className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] border truncate ${statusColor(s.status)}`} data-testid={`cal-shoot-${s.shoot_id}`}>
                          {s.client_name}
                        </button>
                      ))}
                      {dayShoots.length > 3 && <span className="text-[10px] text-slate-500 pl-1">+{dayShoots.length - 3} more</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>
    );
  }

  // Week view
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + i);
    return d;
  });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="glass-card overflow-hidden animate-fadeIn" style={{ animationDelay: '0.2s' }} data-testid="calendar-week-view">
      <div className="flex items-center justify-between p-4 border-b border-white/5">
        <button onClick={() => navigateMonth(-1)} className="text-slate-400 hover:text-white p-1" data-testid="cal-prev"><ChevronLeft size={18} /></button>
        <h3 className="text-white font-heading font-bold text-lg">
          {weekDays[0].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {weekDays[6].toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </h3>
        <button onClick={() => navigateMonth(1)} className="text-slate-400 hover:text-white p-1" data-testid="cal-next"><ChevronRight size={18} /></button>
      </div>
      <div className="overflow-x-auto">
      <div className="grid grid-cols-7 divide-x divide-white/[0.03] min-w-[560px]">
        {weekDays.map((wd, idx) => {
          const dateStr = wd.toISOString().slice(0, 10);
          const dayShoots = shootMap[dateStr] || [];
          const isToday = dateStr === today;
          return (
            <div key={idx} className="min-h-[200px] p-2">
              <div className={`text-center mb-2 pb-2 border-b border-white/5`}>
                <p className="text-[10px] text-slate-500 uppercase">{DAYS[idx]}</p>
                <p className={`text-sm font-bold ${isToday ? 'text-indigo-400' : 'text-white'}`}>{wd.getDate()}</p>
              </div>
              <div className="space-y-1">
                {dayShoots.map(s => (
                  <button key={s.id} onClick={() => onShootClick(s)} className={`w-full text-left p-1.5 rounded border ${statusColor(s.status)}`} data-testid={`cal-shoot-${s.shoot_id}`}>
                    <p className="text-[10px] font-medium truncate">{s.client_name}</p>
                    <p className="text-[9px] opacity-70 truncate">{s.location}</p>
                    {s.reporting_time && <p className="text-[9px] opacity-60">{s.reporting_time}</p>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}

// ================================
// LIST VIEW
// ================================
function ListView({ shoots, onShootClick, formatDate, user }) {
  const statusBadge = (status) => {
    if (status === 'Completed') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (status === 'Cancelled') return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
    return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20';
  };

  return (
    <div className="glass-card overflow-hidden animate-fadeIn" style={{ animationDelay: '0.2s' }} data-testid="shoot-list-view">
      <div className="overflow-x-auto mobile-cards">
        <table className="data-table">
          <thead>
            <tr>
              <th>Shoot</th>
              <th>Client</th>
              <th>Date</th>
              <th>Location</th>
              <th>Team</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shoots.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-500">No shoots found</td></tr>
            ) : shoots.map(s => (
              <tr key={s.id} className="cursor-pointer" onClick={() => onShootClick(s)} data-testid={`shoot-row-${s.shoot_id}`}>
                <td>
                  <div>
                    <span className="text-xs font-mono text-purple-400">{s.shoot_id}</span>
                    {s.shoot_type && <span className="text-[10px] text-slate-500 ml-2">{s.shoot_type}</span>}
                  </div>
                </td>
                <td><span className="text-white text-sm">{s.client_name}</span></td>
                <td><span className="text-slate-300 text-sm">{formatDate(s.shoot_date)}</span></td>
                <td><span className="text-slate-400 text-sm flex items-center gap-1"><MapPin size={12} />{s.location}</span></td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    {s.team?.slice(0, 2).map((t, i) => (
                      <span key={i} className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded text-slate-400">{t.name?.split(' ')[0]}</span>
                    ))}
                    {s.team?.length > 2 && <span className="text-[10px] text-slate-500">+{s.team.length - 2}</span>}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${statusBadge(s.status)}`}>{s.status}</span>
                    {user?.role === 'admin' && <ChangeHistoryButton resource="shoots" recordId={s.shoot_id} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================================
// CREATE SHOOT DIALOG
// ================================
function CreateShootDialog({ clients, employees, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    client_id: '', shoot_date: '', location: '', shoot_type: '',
    team: [], reporting_time: '', brief: '', special_requirements: '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleTeam = (empId) => {
    setForm(prev => ({
      ...prev,
      team: prev.team.includes(empId) ? prev.team.filter(id => id !== empId) : [...prev.team, empId],
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.shoot_type) delete payload.shoot_type;
      await axios.post(`${API}/shoots`, payload, { withCredentials: true });
      toast.success('Shoot planned');
      onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed');
    } finally { setLoading(false); }
  };

  const canSubmit = form.client_id && form.shoot_date && form.location;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-lg">Plan New Shoot</DialogTitle>
        </DialogHeader>
        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}

        <div className="space-y-4 py-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Client *</label>
            <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="shoot-client"><SelectValue placeholder="Select Client" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
                {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name} ({c.client_id})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Shoot Date *</label>
              <Input type="date" value={form.shoot_date} onChange={(e) => set('shoot_date', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-date" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Reporting Time</label>
              <Input type="time" value={form.reporting_time} onChange={(e) => set('reporting_time', e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-reporting-time" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Location *</label>
            <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Studio 3, Andheri West" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-location" />
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Shoot Type</label>
            <Select value={form.shoot_type} onValueChange={(v) => set('shoot_type', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-type"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                {SHOOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Team Assigned</label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="shoot-team-picker">
              {employees.map(e => {
                const selected = form.team.includes(e.employee_id);
                return (
                  <button key={e.employee_id} type="button" onClick={() => toggleTeam(e.employee_id)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${selected ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'}`}
                    data-testid={`team-chip-${e.employee_id}`}
                  >{e.name}</button>
                );
              })}
              {employees.length === 0 && <p className="text-[10px] text-slate-600">No active employees</p>}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1 block">Brief / Instructions</label>
            <Input value={form.brief} onChange={(e) => set('brief', e.target.value)} placeholder="What's the brief?" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-brief" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Special Requirements</label>
            <Input value={form.special_requirements} onChange={(e) => set('special_requirements', e.target.value)} placeholder="Equipment, props, etc." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="shoot-special" />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !canSubmit} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-shoot">{loading ? 'Planning...' : 'Plan Shoot'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================================
// SHOOT DETAIL SHEET
// ================================
function ShootDetailSheet({ shoot, employees, onClose, onUpdate, formatDate, canWrite }) {
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [closeForm, setCloseForm] = useState({ start_time: '', end_time: '', raw_footage_link: '', client_notes: '', internal_notes: '' });
  const [editForm, setEditForm] = useState({
    shoot_date: shoot.shoot_date || '', location: shoot.location || '', shoot_type: shoot.shoot_type || '',
    team: shoot.team?.map(t => t.employee_id) || [], reporting_time: shoot.reporting_time || '',
    brief: shoot.brief || '', special_requirements: shoot.special_requirements || '',
  });

  const isPlanned = shoot.status === 'Planned';

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/shoots/${shoot.shoot_id}/complete`, closeForm, { withCredentials: true });
      toast.success('Shoot completed & logged to Master Sheet');
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/shoots/${shoot.shoot_id}/cancel`, {}, { withCredentials: true });
      toast.success('Shoot cancelled');
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleEdit = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/shoots/${shoot.shoot_id}`, editForm, { withCredentials: true });
      toast.success('Shoot updated');
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/shoots/${shoot.shoot_id}`, { withCredentials: true });
      toast.success('Shoot deleted');
      onUpdate();
    } catch (err) { toast.error('Failed'); }
  };

  const toggleEditTeam = (empId) => {
    setEditForm(prev => ({
      ...prev,
      team: prev.team.includes(empId) ? prev.team.filter(id => id !== empId) : [...prev.team, empId],
    }));
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-purple-400 text-sm">{shoot.shoot_id}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded border ${shoot.status === 'Completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : shoot.status === 'Cancelled' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'}`}>{shoot.status}</span>
          </div>
          <SheetTitle className="text-white font-heading text-xl font-bold" data-testid="shoot-detail-title">{shoot.client_name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5">
          {/* Shoot Info */}
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Shoot Details</h3>
            <div className="space-y-2.5">
              <InfoRow icon={<CalendarDays size={13} className="text-cyan-400" />} label="Date" value={formatDate(shoot.shoot_date)} />
              <InfoRow icon={<MapPin size={13} className="text-rose-400" />} label="Location" value={shoot.location} />
              {shoot.shoot_type && <InfoRow icon={<Camera size={13} className="text-purple-400" />} label="Type" value={shoot.shoot_type} />}
              {shoot.reporting_time && <InfoRow icon={<Clock size={13} className="text-amber-400" />} label="Reporting Time" value={shoot.reporting_time} />}
            </div>
          </div>

          {/* Team */}
          {shoot.team?.length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3 flex items-center gap-2"><Users size={13} className="text-indigo-400" /> Team ({shoot.team.length})</h3>
              <div className="flex flex-wrap gap-1.5">
                {shoot.team.map((t, i) => (
                  <span key={i} className="text-xs bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 px-2 py-1 rounded-full">{t.name} <span className="text-[9px] text-indigo-400/60 font-mono">{t.employee_id}</span></span>
                ))}
              </div>
            </div>
          )}

          {/* Pre-shoot Notes */}
          {(shoot.brief || shoot.special_requirements) && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3 flex items-center gap-2"><FileText size={13} className="text-slate-400" /> Notes</h3>
              {shoot.brief && <div className="mb-2"><p className="text-[10px] text-slate-500 mb-0.5">Brief</p><p className="text-sm text-slate-300">{shoot.brief}</p></div>}
              {shoot.special_requirements && <div><p className="text-[10px] text-slate-500 mb-0.5">Special Requirements</p><p className="text-sm text-slate-300">{shoot.special_requirements}</p></div>}
            </div>
          )}

          {/* Closure Info (if completed) */}
          {shoot.status === 'Completed' && (
            <div className="glass-card p-4 border-emerald-500/10">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-3 flex items-center gap-2"><CheckCircle2 size={13} /> Completion Details</h3>
              <div className="space-y-2.5">
                {shoot.start_time && <InfoRow icon={<Play size={13} className="text-emerald-400" />} label="Start" value={shoot.start_time} />}
                {shoot.end_time && <InfoRow icon={<Clock size={13} className="text-emerald-400" />} label="End" value={shoot.end_time} />}
                {shoot.duration && <InfoRow icon={<Clock size={13} className="text-emerald-400" />} label="Duration" value={shoot.duration} />}
                {shoot.raw_footage_link && (
                  <div className="flex items-center gap-2 text-xs">
                    <Link2 size={13} className="text-cyan-400 shrink-0" />
                    <span className="text-slate-500">Footage:</span>
                    <a href={shoot.raw_footage_link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 truncate">{shoot.raw_footage_link}</a>
                  </div>
                )}
                {shoot.client_notes && <div><p className="text-[10px] text-slate-500">Client Notes</p><p className="text-sm text-slate-300">{shoot.client_notes}</p></div>}
                {shoot.internal_notes && <div><p className="text-[10px] text-slate-500">Internal Notes</p><p className="text-sm text-slate-300">{shoot.internal_notes}</p></div>}
              </div>
            </div>
          )}

          {/* Cancellation reason */}
          {shoot.status === 'Cancelled' && shoot.cancel_reason && (
            <div className="glass-card p-4 border-rose-500/10">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-rose-400 mb-2 flex items-center gap-2"><AlertCircle size={13} /> Cancellation Reason</h3>
              <p className="text-sm text-slate-300">{shoot.cancel_reason}</p>
            </div>
          )}

          {/* Actions for Planned shoots */}
          {isPlanned && !showCloseForm && !showEditForm && canWrite && (
            <div className="space-y-2 pt-2">
              <div className="flex gap-2">
                <Button onClick={() => setShowCloseForm(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="mark-complete-button">
                  <CheckCircle2 size={14} className="mr-1" /> Mark as Completed
                </Button>
                <Button onClick={() => setShowEditForm(true)} variant="outline" className="bg-white/5 border-white/10 text-slate-300 h-9 text-xs" data-testid="edit-shoot-button">
                  <Edit2 size={14} className="mr-1" /> Edit
                </Button>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCancel} variant="outline" disabled={actionLoading} className="flex-1 bg-white/5 border-white/10 text-slate-300 hover:bg-rose-500/10 hover:text-rose-400 h-9 text-xs" data-testid="cancel-shoot-button">
                  <XCircle size={14} className="mr-1" /> Cancel Shoot
                </Button>
                <Button onClick={handleDelete} variant="destructive" className="bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 h-9 text-xs" data-testid="delete-shoot-button">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          )}

          {/* Close/Complete Form */}
          {isPlanned && showCloseForm && (
            <div className="glass-card p-4 space-y-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-2">Complete Shoot</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Start Time</label>
                  <Input type="time" value={closeForm.start_time} onChange={(e) => setCloseForm({ ...closeForm, start_time: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="close-start-time" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">End Time</label>
                  <Input type="time" value={closeForm.end_time} onChange={(e) => setCloseForm({ ...closeForm, end_time: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="close-end-time" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Raw Footage Link</label>
                <Input value={closeForm.raw_footage_link} onChange={(e) => setCloseForm({ ...closeForm, raw_footage_link: e.target.value })} placeholder="https://drive.google.com/..." className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="close-footage-link" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Client Notes</label>
                <Input value={closeForm.client_notes} onChange={(e) => setCloseForm({ ...closeForm, client_notes: e.target.value })} placeholder="Client feedback..." className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="close-client-notes" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Internal Notes</label>
                <Input value={closeForm.internal_notes} onChange={(e) => setCloseForm({ ...closeForm, internal_notes: e.target.value })} placeholder="What worked, what didn't..." className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="close-internal-notes" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowCloseForm(false)} className="bg-white/5 border-white/10 text-white h-8 text-xs">Cancel</Button>
                <Button onClick={handleComplete} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700 flex-1 h-8 text-xs" data-testid="confirm-complete-button">
                  {actionLoading ? 'Saving...' : 'Confirm Complete'}
                </Button>
              </div>
            </div>
          )}

          {/* Edit Form */}
          {isPlanned && showEditForm && (
            <div className="glass-card p-4 space-y-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-2">Edit Shoot</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Shoot Date</label>
                  <Input type="date" value={editForm.shoot_date} onChange={(e) => setEditForm({ ...editForm, shoot_date: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-shoot-date" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Reporting Time</label>
                  <Input type="time" value={editForm.reporting_time} onChange={(e) => setEditForm({ ...editForm, reporting_time: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-reporting-time" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Location</label>
                <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-location" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Type</label>
                <Select value={editForm.shoot_type} onValueChange={(v) => setEditForm({ ...editForm, shoot_type: v })}>
                  <SelectTrigger className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                    {SHOOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Team</label>
                <div className="flex flex-wrap gap-1">
                  {employees.map(e => {
                    const sel = editForm.team.includes(e.employee_id);
                    return (
                      <button key={e.employee_id} type="button" onClick={() => toggleEditTeam(e.employee_id)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${sel ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300' : 'bg-white/5 border-white/10 text-slate-400'}`}
                      >{e.name}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Brief</label>
                <Input value={editForm.brief} onChange={(e) => setEditForm({ ...editForm, brief: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="edit-brief" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowEditForm(false)} className="bg-white/5 border-white/10 text-white h-8 text-xs">Cancel</Button>
                <Button onClick={handleEdit} disabled={actionLoading} className="bg-indigo-600 hover:bg-indigo-700 flex-1 h-8 text-xs" data-testid="confirm-edit-button">
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}

          {/* Delete for non-planned */}
          {!isPlanned && (
            <div className="pt-2 border-t border-white/10">
              <Button onClick={handleDelete} variant="destructive" className="w-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 h-9 text-xs" data-testid="delete-shoot-button">
                <Trash2 size={14} className="mr-1" /> Delete Shoot
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {icon}
      <span className="text-slate-500 min-w-[80px]">{label}</span>
      <span className="text-slate-200">{value}</span>
    </div>
  );
}
